// Texblock.js
import { useRef, useState, useEffect } from "react";
import { EditableMathField, addStyles } from "react-mathquill";

addStyles()

export function NotebookTextBlock({ content, interim, onChange, onFocus }) {
  const pRef = useRef(null);
  const localEditRef = useRef(false);
  // Track the last content we set from props to avoid unnecessary DOM writes
  const lastContentRef = useRef(content);

  useEffect(() => {
    // Skip only if WE just edited via keyboard (localEditRef)
    if (localEditRef.current) {
      localEditRef.current = false;
      lastContentRef.current = content;
      return;
    }

    // Always update DOM when content changes from external source (speech)
    if (pRef.current && lastContentRef.current !== content) {
      lastContentRef.current = content;
      // Save and restore cursor if element is focused
      const isFocused = pRef.current === document.activeElement;
      pRef.current.innerHTML = (content || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br>");
      if (isFocused) {
        // Move cursor to end
        const sel = window.getSelection();
        sel.selectAllChildren(pRef.current);
        sel.collapseToEnd();
      }
    }
  }, [content]);

  const handleBlur = (e) => {
    localEditRef.current = true;
    onChange(e.currentTarget.innerText);
  };

  // Also update on input so speech + keyboard stay in sync
  const handleInput = (e) => {
    localEditRef.current = true;
    onChange(e.currentTarget.innerText);
  };

  return (
    <div
      style={{
        position: "relative",
        borderRadius: 10,
        background: "#ffffff",
        border: "1.5px solid #e8e8e8",
        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
        padding: "14px 16px",
      }}
    >
      {/* Single container — interim renders INSIDE the same box */}
      <p
        ref={pRef}
        contentEditable
        suppressContentEditableWarning
        onFocus={onFocus}
        onBlur={handleBlur}
        onInput={handleInput}
        style={{
          margin: 0,
          fontSize: "20px",
          lineHeight: 1.6,
          color: "#2d2d2d",
          fontFamily: "'Georgia', serif",
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
          outline: "1.5px dashed #ddd",
          borderRadius: 4,
          cursor: "text",
          minHeight: 40,
          padding: "4px 8px",
        }}
      />

      {/* Interim overlay — positioned inside the box, after the text */}
      {interim && (
        <div
          style={{
            pointerEvents: "none",
            fontSize: 16,
            fontStyle: "italic",
            color: "#9ca3af",
            padding: "0 8px 4px 8px",
            fontFamily: "'Georgia', serif",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {interim}
        </div>
      )}

      {/* Label */}
      <div
        style={{
          position: "absolute",
          top: -8,
          left: 12,
          background: "#fff",
          padding: "0 6px",
          fontSize: 10,
          fontWeight: 700,
          color: "#bbb",
          fontFamily: "'Nunito', sans-serif",
          letterSpacing: 1,
          textTransform: "uppercase",
        }}
      >
        Text
      </div>
    </div>
  );
}


export function NotebookMathBlock({ content, interim, onChange, onFocus }) {
  const lines = Array.isArray(content) ? content : [""];
  const mqRefs = useRef([]);
  const lastSpaceRef = useRef(0);
  const ignoreChangeUntilRef = useRef(0);

  const lineKeysRef = useRef([crypto.randomUUID()]);
  while (lineKeysRef.current.length < lines.length) {
    lineKeysRef.current.push(crypto.randomUUID());
  }

  const pushChange = (newLines, newKeys) => {
    if (newKeys) lineKeysRef.current = newKeys;
    onChange(newLines);
  };

  const handleLineChange = (index, mathField) => {
    // Skip changes during enter/backspace operations
    if (Date.now() < ignoreChangeUntilRef.current) return;

    const latex =
      typeof mathField?.latex === "function"
        ? mathField.latex()
        : typeof mathField === "string"
        ? mathField
        : "";

    // Don't update if the value hasn't actually changed
    if (latex === lines[index]) return;

    const newLines = [...lines];
    newLines[index] = latex;
    pushChange(newLines);
  };

  const handleEnter = (index) => {
    // Snapshot current content BEFORE any re-render
    const currentLatex = mqRefs.current[index]?.latex?.() ?? lines[index];
    
    ignoreChangeUntilRef.current = Date.now() + 200;

    const newLines = [...lines];
    newLines[index] = currentLatex; // preserve what's actually in the field
    newLines.splice(index + 1, 0, "");

    const newKeys = [...lineKeysRef.current];
    newKeys.splice(index + 1, 0, crypto.randomUUID());

    pushChange(newLines, newKeys);
    setTimeout(() => {
      ignoreChangeUntilRef.current = 0;
      mqRefs.current[index + 1]?.focus();
    }, 200);
  };

  const handleBackspaceOnEmpty = (index) => {
    if (lines.length === 1) return;
    
    ignoreChangeUntilRef.current = Date.now() + 200;

    const newLines = lines.filter((_, i) => i !== index);
    const newKeys = lineKeysRef.current.filter((_, i) => i !== index);

    pushChange(newLines, newKeys);
    setTimeout(() => {
      ignoreChangeUntilRef.current = 0;
      mqRefs.current[index - 1]?.focus();
    }, 200);
  };

  const setupDoubleSpaceTab = (mf) => {
    const el = mf.el();
    if (!el) return;
    el.addEventListener("keydown", (evt) => {
      if (evt.key !== " " && evt.key !== "Spacebar") return;
      const now = Date.now();
      const gap = now - lastSpaceRef.current;
      lastSpaceRef.current = now;
      if (gap < 400 && gap > 0) {
        evt.preventDefault();
        evt.stopPropagation();
        mf.keystroke("Backspace");
        mf.keystroke("Tab");
        lastSpaceRef.current = 0;
      }
    });
  };

  return (
    <div
      style={{
        position: "relative",
        borderRadius: 10,
        background: "#ffffff",
        border: "1.5px solid #e8e8e8",
        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
        padding: "14px 16px",
        width: "100%",
        boxSizing: "border-box",
        overflow: "visible",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          width: "100%",
          overflowY: "visible",
        }}
      >
        <style>{`
          .nb-math-line .mq-editable-field {
            display: block !important;
            width: 100% !important;
            overflow: visible !important;
          }
          .nb-math-line .mq-editable-field .mq-root-block {
            white-space: normal !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            overflow: visible !important;
          }
        `}</style>

        {lines.map((latex, index) => (
          <div
            key={lineKeysRef.current[index]}
            className="nb-math-line"
            style={{ width: "100%", overflowY: "visible" }}
          >
            <EditableMathField
              latex={latex}
              onChange={(mf) => handleLineChange(index, mf)}
              mathquillDidMount={(mf) => {
                mqRefs.current[index] = mf;
                setupDoubleSpaceTab(mf);
              }}
              config={{
                spaceBehavesLikeTab: false,
                handlers: {
                  enter: () => handleEnter(index),
                  deleteOutOf: () => {
                    if (lines[index] === "") handleBackspaceOnEmpty(index);
                  },
                },
              }}
              onFocus={onFocus}
              style={{
                width: "100%",
                minHeight: 28,
                fontSize: "20px",
                fontFamily: "'Georgia', serif",
                border: "none",
                borderBottom: "1px dashed #e0e0e0",
                borderRadius: 0,
                padding: "2px 4px",
                boxSizing: "border-box",
                cursor: "text",
                display: "block",
              }}
            />
          </div>
        ))}
      </div>

      {interim && (
        <div
          style={{
            pointerEvents: "none",
            fontSize: 16,
            fontStyle: "italic",
            color: "#9ca3af",
            padding: "8px 4px 0 4px",
            fontFamily: "'Georgia', serif",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {interim}
        </div>
      )}

      <div
        style={{
          position: "absolute",
          top: -8,
          left: 12,
          background: "#fff",
          padding: "0 6px",
          fontSize: 10,
          fontWeight: 700,
          color: "#bbb",
          fontFamily: "'Nunito', sans-serif",
          letterSpacing: 1,
          textTransform: "uppercase",
        }}
      >
        Math
      </div>
    </div>
  );
}